"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Card, CardBody, CardHeader, Badge, Input, Row, Col, Label } from "reactstrap"
import { FaEye, FaSearch, FaCar, FaCheckCircle } from "react-icons/fa"
import DataTable, { type TableColumn } from "react-data-table-component"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { Btn } from "../../../AbstractElements"
import { VehicleVisitService } from "../../../Service/TicketingService"
import type { VehicleVisit, PaginatedResponse } from "./types"
import { toast } from "react-toastify"

interface Props {
  onViewDetails: (visit: VehicleVisit) => void
  onRecordEntry: () => void
  onVerifyVehicle: () => void
  refreshTrigger: boolean
}

const VehicleVisitList: React.FC<Props> = ({ onViewDetails, onRecordEntry, onVerifyVehicle, refreshTrigger }) => {
  const [visits, setVisits] = useState<VehicleVisit[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [dateFrom, setDateFrom] = useState<Date | null>(null)
  const [dateTo, setDateTo] = useState<Date | null>(null)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 10,
  })

  const fetchVisits = async (
    page = 1,
    searchTerm = "",
    status = "",
    fromDate: Date | null = null,
    toDate: Date | null = null,
  ) => {
    try {
      setLoading(true)
      const response: PaginatedResponse<VehicleVisit> = await VehicleVisitService.getAll(
        page,
        pagination.pageSize,
        searchTerm,
        status,
      )
      setVisits(response.results)
      setPagination({
        currentPage: response.current_page,
        totalPages: response.total_pages,
        totalCount: response.count,
        pageSize: response.page_size,
      })
    } catch (error) {
      console.error("Failed to fetch vehicle visits:", error)
      toast.error("Failed to load vehicle visits")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchVisits(1, search, statusFilter, dateFrom, dateTo)
  }, [refreshTrigger])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    fetchVisits(1, search, statusFilter, dateFrom, dateTo)
  }

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status)
    fetchVisits(1, search, status, dateFrom, dateTo)
  }

  const handleDateFilter = () => {
    fetchVisits(1, search, statusFilter, dateFrom, dateTo)
  }

  const handleClearDates = () => {
    setDateFrom(null)
    setDateTo(null)
    fetchVisits(1, search, statusFilter, null, null)
  }

  const getStatusBadge = (status: string) => {
    const statusColors = {
      entered: "warning",
      exited: "success",
      cancelled: "danger",
    }
    return (
      <Badge color={statusColors[status as keyof typeof statusColors] || "secondary"} pill>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    )
  }

  const handlePageChange = (page: number) => {
    fetchVisits(page, search, statusFilter, dateFrom, dateTo)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPagination((prev) => ({ ...prev, pageSize: newPerPage }))
    fetchVisits(page, search, statusFilter, dateFrom, dateTo)
  }

  const columns: TableColumn<VehicleVisit>[] = [
    {
      name: "#",
      selector: (row, index) => (pagination.currentPage - 1) * pagination.pageSize + (index || 0) + 1,
      width: "80px",
    },
    {
      name: "Plate Number",
      selector: (row) => row.plate,
      sortable: true,
      cell: (row) => <strong>{row.plate}</strong>,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => getStatusBadge(row.status),
    },
    {
      name: "Gate",
      selector: (row) => row.gate_assignment_info?.gate || "",
      sortable: true,
      cell: (row) => (
        <Badge color="info" pill>
          {row.gate_assignment_info?.gate.replace("_", " ").toUpperCase() || "N/A"}
        </Badge>
      ),
    },
    {
      name: "Entry Time",
      selector: (row) => row.entered_at || "",
      sortable: true,
      cell: (row) => (
        <div>
          {row.entered_at ? (
            <>
              <div>{new Date(row.entered_at).toLocaleDateString()}</div>
              <small className="text-muted">{new Date(row.entered_at).toLocaleTimeString()}</small>
            </>
          ) : (
            <span className="text-muted">-</span>
          )}
        </div>
      ),
    },
    {
      name: "Duration",
      selector: (row) => row.duration_minutes || 0,
      sortable: true,
      cell: (row) => (
        <div>
          {row.duration_minutes ? <span>{row.duration_minutes} min</span> : <span className="text-muted">-</span>}
        </div>
      ),
    },
    {
      name: "Amount",
      selector: (row) => row.amount || 0,
      sortable: true,
      cell: (row) => (
        <div>
          {row.amount ? <strong>FRW {row.amount.toLocaleString()}</strong> : <span className="text-muted">-</span>}
        </div>
      ),
    },
    {
      name: "Gate Operator",
      selector: (row) => row.gate_assignment_info?.user_name || "",
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <Btn color="info" size="sm" onClick={() => onViewDetails(row)} title="View Details">
          <FaEye />
        </Btn>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ]

  return (
    <Card>
      <CardHeader>
        <Row className="align-items-center">
          <Col md="6">
            <h5 className="mb-0">Vehicle Visits ({pagination.totalCount})</h5>
          </Col>
          <Col md="6" className="text-end">
            <div className="d-flex gap-2 justify-content-end">
              <Btn color="success" size="sm" onClick={onVerifyVehicle}>
                <FaCheckCircle className="me-2" />
                Verify Vehicle
              </Btn>
              <Btn color="primary" size="sm" onClick={onRecordEntry}>
                <FaCar className="me-2" />
                Record Entry
              </Btn>
            </div>
          </Col>
        </Row>
      </CardHeader>
      <CardBody>
        {/* Search and Filters */}
        <form onSubmit={handleSearch} className="mb-3">
          <Row className="align-items-end">
            <Col md="3">
              <Label>Search</Label>
              <div className="input-group">
                <Input
                  type="text"
                  placeholder="Search visits..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button className="btn btn-outline-secondary btn-sm" type="submit">
                  <FaSearch />
                </button>
              </div>
            </Col>
            <Col md="2">
              <Label>Status</Label>
              <Input type="select" value={statusFilter} onChange={(e) => handleStatusFilter(e.target.value)}>
                <option value="">All Status</option>
                <option value="entered">Entered</option>
                <option value="exited">Exited</option>
                <option value="cancelled">Cancelled</option>
              </Input>
            </Col>
            <Col md="2">
              <Label>From Date</Label>
              <DatePicker
                selected={dateFrom}
                onChange={(date) => setDateFrom(date)}
                placeholderText="From date"
                className="form-control"
                dateFormat="yyyy-MM-dd"
                isClearable
              />
            </Col>
            <Col md="2">
              <Label>To Date</Label>
              <DatePicker
                selected={dateTo}
                onChange={(date) => setDateTo(date)}
                placeholderText="To date"
                className="form-control"
                dateFormat="yyyy-MM-dd"
                isClearable
                // minDate={dateFrom}
              />
            </Col>
            <Col md="3">
              <div className="d-flex gap-1">
                <Btn color="secondary" size="sm" onClick={handleDateFilter}>
                  Filter
                </Btn>
                {(dateFrom || dateTo) && (
                  <Btn color="outline-secondary" size="sm" onClick={handleClearDates}>
                    Clear
                  </Btn>
                )}
              </div>
            </Col>
          </Row>
        </form>

        {/* Active Filters Display */}
        {(search || statusFilter || dateFrom || dateTo) && (
          <div className="mb-3">
            <div className="d-flex flex-wrap gap-2">
              {search && (
                <Badge color="info" pill>
                  Search: {search}
                </Badge>
              )}
              {statusFilter && (
                <Badge color="primary" pill>
                  Status: {statusFilter}
                </Badge>
              )}
              {dateFrom && (
                <Badge color="success" pill>
                  From: {dateFrom.toLocaleDateString()}
                </Badge>
              )}
              {dateTo && (
                <Badge color="success" pill>
                  To: {dateTo.toLocaleDateString()}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* DataTable */}
        <DataTable
          columns={columns}
          data={visits}
          progressPending={loading}
          pagination
          paginationServer
          paginationTotalRows={pagination.totalCount}
          paginationDefaultPage={pagination.currentPage}
          paginationPerPage={pagination.pageSize}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          noDataComponent="No vehicle visits found"
          striped
          highlightOnHover
          responsive
        />
      </CardBody>
    </Card>
  )
}

export default VehicleVisitList
